// EspaciosEmpresarial.tsx — Co Marca — ruta: /espacios/empresarial
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Building2, Shield, Wifi, Users, BarChart3, Headphones,
    ChevronRight, ArrowLeft, CheckCircle2, Phone, Mail,
    CalendarCheck, Layers, Zap, Globe,
} from 'lucide-react';
import HexagonCornerDecoration from '../../components/common/HexagonCornerDecoration';

const colors = {
    primary: '#c1348b',
    primaryDark: '#8f1f64',
    secondary: '#61257b',
    secondaryLight: '#8b3fa8',
    accent: '#ffd97c',
    accentDark: '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
* { box-sizing: border-box; margin: 0; padding: 0; }

@keyframes blobFloat {
  0%,100% { border-radius:60% 40% 30% 70%/60% 30% 70% 40%; }
  50%      { border-radius:30% 60% 70% 40%/50% 60% 30% 60%; }
}
@keyframes sectionFadeUp {
  from { opacity:0; transform:translateY(22px); }
  to   { opacity:1; transform:translateY(0); }
}
@keyframes pulseEmp {
  0%,100% { box-shadow:0 0 0 0 rgba(234, 132, 6,.35); }
  50%      { box-shadow:0 0 0 6px rgba(234, 132, 6,0); }
}

.ee-hero-blob { animation: blobFloat 10s ease-in-out infinite; }
.ee-section  { animation: sectionFadeUp .7s cubic-bezier(.16,1,.3,1) both; }
.ee-card {
  transition: all .25s cubic-bezier(.16,1,.3,1);
  border-top: 4px solid transparent;
}
.ee-card:hover {
  transform: translateY(-4px);
  border-top-color: var(--card-accent);
  box-shadow: 0 14px 40px var(--card-shadow) !important;
}
.ee-card:hover .ee-icon-box {
  background: var(--card-accent) !important;
  color: #fff !important;
}
.ee-timeline-dot { transition: all .2s; }
.ee-timeline-item:hover .ee-timeline-dot { transform: scale(1.3); }
.ee-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(234, 132, 6,.38) !important; }

@media (max-width:900px) {
  .ee-hero-layout  { flex-direction: column !important; }
  .ee-hero-visual  { display: none !important; }
  .ee-sol-grid     { grid-template-columns: 1fr 1fr !important; }
  .ee-plans-grid   { grid-template-columns: 1fr !important; }
  .ee-cta-row      { flex-direction: column !important; align-items: flex-start !important; }
  .ee-steps-grid   { grid-template-columns: 1fr !important; }
}
@media (max-width:600px) {
  .ee-sol-grid   { grid-template-columns: 1fr !important; }
  .ee-stats-row  { flex-direction: column !important; gap: 10px !important; }
}
`;

// ── Datos ───────────────────────────────────────────────────────────────
const SOLUTIONS = [
    { icon: <Building2 size={20}/>,  label: 'Oficinas privadas',      desc: 'Espacios cerrados para equipos de 2 a 20 personas. Llave en mano, sin obras ni esperas.', color: colors.accentDark },
    { icon: <Shield size={20}/>,     label: 'Acceso seguro 24/7',     desc: 'Control de acceso biométrico, CCTV y recepción para tus colaboradores y visitas.', color: colors.primary },
    { icon: <Wifi size={20}/>,       label: 'Red dedicada 1 Gbps',    desc: 'Ancho de banda exclusivo para tu empresa, con SLA de disponibilidad del 99.9%.', color: colors.secondary },
    { icon: <CalendarCheck size={20}/>, label: 'Salas de reuniones',  desc: 'Reserva hasta 8 salas equipadas con TV 4K, videoconferencia y pizarrón inteligente.', color: colors.secondaryLight },
    { icon: <BarChart3 size={20}/>,  label: 'Panel de gestión',       desc: 'Administra el uso de espacios, costos por equipo y reportes en tiempo real desde tu dashboard.', color: colors.accentDark },
    { icon: <Headphones size={20}/>, label: 'Soporte dedicado',       desc: 'Un account manager exclusivo para tu empresa disponible por teléfono y WhatsApp.', color: colors.primary },
    { icon: <Layers size={20}/>,     label: 'Estrategia híbrida',     desc: 'Combina oficina privada y escritorios de coworking para optimizar tu portafolio inmobiliario.', color: colors.secondary },
    { icon: <Globe size={20}/>,      label: 'Presencia de marca',     desc: 'Señalética personalizada, dirección fiscal en Calacoto y recepción con tu nombre corporativo.', color: colors.secondaryLight },
];

const PLANS = [
    {
        name: 'Equipo Pequeño',
        capacity: 'Hasta 5 personas',
        price: '1,400',
        unit: 'mes',
        desc: 'Ideal para startups y consultoras que necesitan privacidad sin pagar una oficina entera.',
        features: ['5 puestos fijos asignados', 'WiFi dedicado 300 Mbps', 'Sala reuniones 8 hrs/mes', 'Recepción compartida', 'Panel de gestión básico'],
        color: colors.secondary,
        badge: null,
    },
    {
        name: 'Corporativo',
        capacity: 'Hasta 15 personas',
        price: '3,800',
        unit: 'mes',
        desc: 'La solución completa para empresas que necesitan presencia, privacidad y flexibilidad real.',
        features: ['15 puestos fijos + 5 flex', 'WiFi dedicado 500 Mbps', 'Sala reuniones ilimitada', 'Recepcionista exclusivo', 'Señalética de marca', 'Panel gestión avanzado', 'Account manager'],
        color: colors.accentDark,
        badge: 'Recomendado',
    },
    {
        name: 'Enterprise',
        capacity: '+20 personas',
        price: 'A medida',
        unit: '',
        desc: 'Para grandes equipos que necesitan un piso completo o soluciones totalmente personalizadas.',
        features: ['Piso completo dedicado', 'WiFi 1 Gbps exclusivo', 'Todas las salas incluidas', 'Branding total del espacio', 'Dirección fiscal premium', 'SLA 99.9% disponibilidad', 'Soporte 24/7'],
        color: colors.primaryDark,
        badge: 'Piso completo',
    },
];

const STATS = [
    { value: '300+', label: 'Empresas atendidas', color: colors.accentDark },
    { value: '8',    label: 'Salas de reuniones', color: colors.primary },
    { value: '1G',   label: 'Mbps disponibles', color: colors.secondary },
    { value: '99.9%',label: 'Uptime garantizado', color: colors.secondaryLight },
];

const STEPS = [
    { n: '01', title: 'Diagnóstico gratuito', desc: 'Nuestro equipo analiza tus necesidades de espacio, headcount y presupuesto en una reunión sin compromiso.', color: colors.accentDark },
    { n: '02', title: 'Propuesta personalizada', desc: 'Recibirás un plan a medida con opciones de oficinas, configuración y costos detallados en 48 horas.', color: colors.primary },
    { n: '03', title: 'Setup y mudanza', desc: 'Nos encargamos de todo: señalética, red, mobiliario y accesos. Tu equipo llega y trabaja desde el primer día.', color: colors.secondary },
    { n: '04', title: 'Gestión continua', desc: 'Tu account manager hace seguimiento mensual, ajusta el plan según el crecimiento de tu empresa y resuelve cualquier solicitud.', color: colors.secondaryLight },
];

const CLIENTS = [
    { name:'Banco Nacional', initial:'BN', color: colors.secondary },
    { name:'Entel Bolivia',  initial:'EB', color: colors.primary },
    { name:'Tigo Business',  initial:'TB', color: colors.accentDark },
    { name:'Fundación JALA', initial:'JA', color: colors.secondaryLight },
    { name:'YPFB',           initial:'YP', color: colors.primaryDark },
    { name:'Nexos Bolivia',  initial:'NX', color: colors.secondary },
];

// ── MAIN ───────────────────────────────────────────────────────────────
const EspaciosEmpresarial: React.FC = () => {
    const navigate = useNavigate();
    const [heroVisible, setHeroVisible] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setHeroVisible(true), 80);
        return () => clearTimeout(t);
    }, []);

    return (
        <div style={{ fontFamily:"'DM Sans',sans-serif", background:'#fff', overflowX:'hidden' }}>
            <style>{css}</style>

            {/* ══ HERO ══ */}
            <section style={{
                minHeight:'88vh',
                background:'linear-gradient(135deg, #fff 0%, #fff8f0 55%, #fdecd0 100%)',
                display:'flex', alignItems:'center',
                position:'relative', overflow:'hidden',
                paddingTop:'clamp(92px, 12vw, 115px)',
            }}>
                {/* Blob */}
                <div className="ee-hero-blob" style={{
                    position:'absolute', top:'-100px', right:'-120px',
                    width:'540px', height:'540px',
                    background:`linear-gradient(135deg, ${colors.accentDark}20, ${colors.accent}15)`,
                    pointerEvents:'none',
                }}/>
                {/* Malla hexagonal animada de izquierda a derecha con degradé suave */}
                <HexagonCornerDecoration
                    variant="horizontal-degrade"
                    direction="left-to-right"
                    opacity={0.65}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        zIndex: 0,
                    }}
                />

                <div style={{ maxWidth:'1180px', margin:'0 auto', padding:'0 clamp(20px,4vw,40px)', width:'100%' }}>
                    <div className="ee-hero-layout" style={{ display:'flex', alignItems:'center', gap:'64px' }}>

                        {/* LEFT */}
                        <div style={{ flex:1, maxWidth:'580px',
                            opacity: heroVisible ? 1 : 0,
                            transform: heroVisible ? 'translateY(0)' : 'translateY(30px)',
                            transition:'opacity .9s ease, transform .9s cubic-bezier(.16,1,.3,1)',
                        }}>
                            {/* Back */}
                            <button onClick={() => navigate(-1)} style={{
                                display:'inline-flex', alignItems:'center', gap:'6px',
                                background:'transparent', border:'none', cursor:'pointer',
                                color:'#94a3b8', fontSize:'.8rem', fontWeight:'600',
                                fontFamily:"'DM Sans',sans-serif", marginBottom:'24px', padding:0,
                            }}
                                onMouseEnter={e => e.currentTarget.style.color = colors.accentDark}
                                onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                            >
                                <ArrowLeft size={14}/> Volver
                            </button>

                            {/* Breadcrumb */}
                            <nav style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'600', color:'#94a3b8', marginBottom:'18px' }}>
                                <a href="/" style={{ color:'#94a3b8', textDecoration:'none' }}
                                    onMouseEnter={e=>e.currentTarget.style.color=colors.accentDark}
                                    onMouseLeave={e=>e.currentTarget.style.color='#94a3b8'}>Inicio</a>
                                <ChevronRight size={10} color="#d1d5db"/>
                                <span style={{ color: colors.accentDark, fontWeight:'700' }}>Empresarial</span>
                            </nav>

                            {/* Badge */}
                            <div style={{
                                display:'inline-flex', alignItems:'center', gap:'8px',
                                background:`${colors.accentDark}12`, border:`1.5px solid ${colors.accentDark}28`,
                                borderRadius:'100px', padding:'6px 16px', marginBottom:'20px',
                                animation:'pulseEmp 2.5s infinite',
                            }}>
                                <span style={{ width:'7px', height:'7px', borderRadius:'50%', background: colors.accentDark, display:'inline-block' }}/>
                                <span style={{ fontSize:'.76rem', fontWeight:'700', color: colors.accentDark, letterSpacing:'.08em', textTransform:'uppercase' }}>
                                    Soluciones Empresariales
                                </span>
                            </div>

                            <h1 style={{
                                fontSize:'clamp(2.2rem,4.5vw,3.4rem)', fontWeight:'900',
                                color:'#0f172a', lineHeight:1.05, letterSpacing:'-.025em', marginBottom:'22px',
                            }}>
                                Oficinas que{' '}
                                <span style={{ color: colors.accentDark }}>escalan</span>{' '}
                                con tu empresa
                            </h1>

                            <p style={{ fontSize:'1.05rem', color:'#475569', lineHeight:1.85, marginBottom:'32px', maxWidth:'500px' }}>
                                Soluciones de nivel empresarial para impulsar tu estrategia híbrida. Combina oficinas privadas, coworking y tecnología de gestión para optimizar cada metro cuadrado de tu presupuesto inmobiliario.
                            </p>

                            {/* Stats en fila */}
                            <div className="ee-stats-row" style={{ display:'flex', gap:'16px', marginBottom:'36px', flexWrap:'wrap' }}>
                                {STATS.map((s, i) => (
                                    <React.Fragment key={s.label}>
                                        <div style={{ textAlign:'center' }}>
                                            <div style={{ fontSize:'1.5rem', fontWeight:'900', color: s.color, lineHeight:1 }}>{s.value}</div>
                                            <div style={{ fontSize:'.68rem', color:'#94a3b8', fontWeight:'600', marginTop:'2px' }}>{s.label}</div>
                                        </div>
                                        {i < STATS.length - 1 && (
                                            <div style={{ width:'1px', background:'#f1f5f9', alignSelf:'stretch' }}/>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>

                            {/* Barra colores */}
                            <div style={{ display:'flex', gap:'5px', marginBottom:'28px' }}>
                                {[colors.accentDark, colors.primary, colors.secondary, colors.secondaryLight].map((c,i) => (
                                    <div key={i} style={{ height:'3px', width:i===0?'28px':'10px', borderRadius:'3px', background:c }}/>
                                ))}
                            </div>

                            {/* CTAs */}
                            <div style={{ display:'flex', gap:'12px', flexWrap:'wrap' }}>
                                <a href="/contacto" className="ee-btn-primary" style={{
                                    display:'inline-flex', alignItems:'center', gap:'8px',
                                    background:`linear-gradient(135deg, ${colors.accentDark}, #b36200)`,
                                    color:'#fff', borderRadius:'13px', padding:'13px 28px',
                                    fontWeight:'800', fontSize:'.95rem', textDecoration:'none',
                                    boxShadow:`0 6px 20px ${colors.accentDark}38`, transition:'all .22s',
                                    fontFamily:"'DM Sans',sans-serif",
                                }}>
                                    Solicitar diagnóstico gratuito →
                                </a>
                                <a href="tel:+59176204601" style={{
                                    display:'inline-flex', alignItems:'center', gap:'8px',
                                    background:'#f8fafc', border:'1.5px solid #e2e8f0',
                                    color:'#374151', borderRadius:'13px', padding:'13px 22px',
                                    fontWeight:'700', fontSize:'.9rem', textDecoration:'none', transition:'all .22s',
                                }}
                                    onMouseEnter={e=>e.currentTarget.style.background='#f1f5f9'}
                                    onMouseLeave={e=>e.currentTarget.style.background='#f8fafc'}
                                >
                                    <Phone size={15} color={colors.accentDark}/> Llamar ahora
                                </a>
                            </div>
                        </div>

                        {/* RIGHT — visual */}
                        <div className="ee-hero-visual" style={{
                            flex:1, maxWidth:'500px',
                            opacity: heroVisible ? 1 : 0,
                            transform: heroVisible ? 'translateX(0)' : 'translateX(40px)',
                            transition:'opacity 1s ease .15s, transform 1s cubic-bezier(.16,1,.3,1) .15s',
                        }}>
                            <div style={{
                                width:'100%', aspectRatio:'4/3', borderRadius:'28px',
                                background:`linear-gradient(145deg, #7a4800, ${colors.accentDark} 50%, ${colors.accent})`,
                                display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'12px',
                                boxShadow:`0 28px 72px ${colors.accentDark}28, 0 8px 24px rgba(0,0,0,.08)`,
                                position:'relative', overflow:'hidden',
                            }}>
                                <div style={{ position:'absolute', top:'-40px', right:'-40px', width:'220px', height:'220px', background:'rgba(255,255,255,.08)', borderRadius:'50%' }}/>
                                <div style={{ position:'absolute', bottom:'-60px', left:'-40px', width:'260px', height:'260px', background:'rgba(255,255,255,.05)', borderRadius:'50%' }}/>
                                <div style={{ fontSize:'5rem', filter:'drop-shadow(0 4px 20px rgba(0,0,0,.15))' }}>🏢</div>
                                <div style={{ background:'rgba(255,255,255,.18)', backdropFilter:'blur(12px)', borderRadius:'14px', padding:'10px 22px', border:'1px solid rgba(255,255,255,.28)', color:'#fff', fontWeight:'700', fontSize:'.9rem' }}>
                                    Empresarial · Co Marca
                                </div>
                                {/* Badge flotante izq */}
                                <div style={{
                                    position:'absolute', bottom:'20px', left:'-20px',
                                    background:'#fff', borderRadius:'16px', padding:'12px 16px',
                                    boxShadow:'0 12px 32px rgba(0,0,0,.1)',
                                    display:'flex', alignItems:'center', gap:'10px',
                                }}>
                                    <Users size={16} color={colors.secondary}/>
                                    <div>
                                        <div style={{ fontWeight:'800', fontSize:'.9rem', color:'#0f172a' }}>300+ empresas</div>
                                        <div style={{ fontSize:'.66rem', color:'#94a3b8' }}>ya confían en nosotros</div>
                                    </div>
                                </div>
                                {/* Badge top der */}
                                <div style={{
                                    position:'absolute', top:'20px', right:'-18px',
                                    background:'#fff', borderRadius:'14px', padding:'10px 14px',
                                    boxShadow:'0 8px 24px rgba(0,0,0,.09)',
                                    display:'flex', alignItems:'center', gap:'8px',
                                }}>
                                    <Zap size={14} color={colors.accentDark}/>
                                    <div style={{ fontSize:'.75rem', fontWeight:'700', color:'#0f172a' }}>Setup en 48 hs</div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ══ SOLUCIONES ══ */}
            <section className="ee-section" style={{ background:'#fff', padding:'80px clamp(20px,4vw,40px)' }}>
                <div style={{ maxWidth:'1180px', margin:'0 auto' }}>
                    <div style={{ textAlign:'center', marginBottom:'52px' }}>
                        <div style={{ display:'inline-flex', alignItems:'center', gap:'10px', marginBottom:'14px' }}>
                            <div style={{ height:'2px', width:'36px', background: colors.accentDark, borderRadius:'2px' }}/>
                            <span style={{ fontSize:'.75rem', fontWeight:'700', letterSpacing:'.14em', textTransform:'uppercase', color: colors.accentDark }}>
                                Soluciones incluidas
                            </span>
                            <div style={{ height:'2px', width:'36px', background: colors.accentDark, borderRadius:'2px' }}/>
                        </div>
                        <h2 style={{ fontSize:'clamp(1.8rem,3vw,2.6rem)', fontWeight:'900', color:'#0f172a', letterSpacing:'-.02em' }}>
                            Todo para que tu empresa{' '}
                            <span style={{ color: colors.accentDark }}>funcione sin fricciones</span>
                        </h2>
                    </div>

                    <div className="ee-sol-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'16px' }}>
                        {SOLUTIONS.map((s, i) => (
                            <div key={s.label} className="ee-card" style={{
                                '--card-accent': s.color,
                                '--card-shadow': `${s.color}18`,
                                background:'#fff', borderRadius:'18px', padding:'24px',
                                boxShadow:'0 2px 12px rgba(0,0,0,.04)',
                                animationDelay:`${i * .06}s`,
                            } as React.CSSProperties}>
                                <div className="ee-icon-box" style={{
                                    width:'44px', height:'44px', borderRadius:'12px',
                                    background:`${s.color}12`, color: s.color,
                                    display:'flex', alignItems:'center', justifyContent:'center',
                                    marginBottom:'14px', transition:'all .22s',
                                }}>
                                    {s.icon}
                                </div>
                                <h3 style={{ fontSize:'.9rem', fontWeight:'800', color:'#0f172a', marginBottom:'6px' }}>{s.label}</h3>
                                <p style={{ fontSize:'.78rem', color:'#64748b', lineHeight:1.6 }}>{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ══ PLANES ══ */}
            <section className="ee-section" style={{ background:'#fff', padding:'80px clamp(20px,4vw,40px)' }}>
                <div style={{ maxWidth:'1180px', margin:'0 auto' }}>
                    <div style={{ textAlign:'center', marginBottom:'52px' }}>
                        <div style={{ display:'inline-flex', alignItems:'center', gap:'10px', marginBottom:'14px' }}>
                            <div style={{ height:'2px', width:'36px', background: colors.primary, borderRadius:'2px' }}/>
                            <span style={{ fontSize:'.75rem', fontWeight:'700', letterSpacing:'.14em', textTransform:'uppercase', color: colors.primary }}>
                                Planes corporativos
                            </span>
                            <div style={{ height:'2px', width:'36px', background: colors.primary, borderRadius:'2px' }}/>
                        </div>
                        <h2 style={{ fontSize:'clamp(1.8rem,3vw,2.6rem)', fontWeight:'900', color:'#0f172a', letterSpacing:'-.02em' }}>
                            El plan correcto para{' '}
                            <span style={{ color: colors.primary }}>cada etapa</span>
                        </h2>
                        <p style={{ fontSize:'.95rem', color:'#64748b', marginTop:'10px' }}>Todos los planes incluyen soporte dedicado y ajuste trimestral sin penalidades.</p>
                    </div>

                    <div className="ee-plans-grid" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'20px', alignItems:'stretch' }}>
                        {PLANS.map((plan) => {
                            const isRec = plan.badge === 'Recomendado';
                            return (
                                <div key={plan.name} style={{
                                    background: isRec ? `linear-gradient(145deg, ${colors.accentDark}06, #fff)` : '#fff',
                                    borderRadius:'22px',
                                    border: isRec ? `2px solid ${colors.accentDark}45` : '1.5px solid #f1f5f9',
                                    padding:'28px 24px',
                                    boxShadow: isRec ? `0 10px 36px ${colors.accentDark}16` : '0 2px 12px rgba(0,0,0,.04)',
                                    position:'relative', display:'flex', flexDirection:'column',
                                    transition:'all .25s',
                                }}
                                    onMouseEnter={e=>e.currentTarget.style.transform='translateY(-3px)'}
                                    onMouseLeave={e=>e.currentTarget.style.transform='translateY(0)'}
                                >
                                    {plan.badge && (
                                        <span style={{
                                            position:'absolute', top:'-11px', left:'50%', transform:'translateX(-50%)',
                                            background: isRec
                                                ? `linear-gradient(135deg, ${colors.accentDark}, #b36200)`
                                                : `linear-gradient(135deg, ${colors.primaryDark}, ${colors.primary})`,
                                            color:'#fff', fontSize:'.65rem', fontWeight:'800',
                                            letterSpacing:'.08em', textTransform:'uppercase',
                                            borderRadius:'100px', padding:'3px 14px', whiteSpace:'nowrap',
                                        }}>{plan.badge}</span>
                                    )}
                                    <div style={{ marginBottom:'8px' }}>
                                        <div style={{ fontSize:'.62rem', fontWeight:'700', color: plan.color, textTransform:'uppercase', letterSpacing:'.1em', marginBottom:'4px' }}>
                                            {plan.name}
                                        </div>
                                        <div style={{ fontSize:'.75rem', color:'#94a3b8', fontWeight:'600', marginBottom:'10px' }}>
                                            {plan.capacity}
                                        </div>
                                        <div style={{ display:'flex', alignItems:'baseline', gap:'4px', marginBottom:'8px' }}>
                                            <span style={{ fontSize:'2rem', fontWeight:'900', color:'#0f172a', letterSpacing:'-.03em' }}>{plan.price}</span>
                                            {plan.unit && <span style={{ fontSize:'.72rem', color:'#94a3b8', fontWeight:'600' }}>Bs./{plan.unit}</span>}
                                        </div>
                                        <p style={{ fontSize:'.78rem', color:'#64748b', lineHeight:1.55 }}>{plan.desc}</p>
                                    </div>
                                    <div style={{ height:'1.5px', background:'#f1f5f9', margin:'16px 0' }}/>
                                    <ul style={{ listStyle:'none', display:'flex', flexDirection:'column', gap:'9px', marginBottom:'22px', flex:1 }}>
                                        {plan.features.map(f => (
                                            <li key={f} style={{ display:'flex', alignItems:'center', gap:'9px', fontSize:'.82rem', color:'#374151', fontWeight:'500' }}>
                                                <CheckCircle2 size={14} color={plan.color} style={{ flexShrink:0 }}/>
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                    <a href="/contacto" style={{
                                        display:'block', textAlign:'center', marginTop:'auto',
                                        background: isRec ? `linear-gradient(135deg, ${colors.accentDark}, #b36200)` : `${plan.color}14`,
                                        color: isRec ? '#fff' : plan.color,
                                        borderRadius:'11px', padding:'11px',
                                        fontWeight:'800', fontSize:'.88rem', textDecoration:'none',
                                        boxShadow: isRec ? `0 4px 14px ${colors.accentDark}30` : 'none',
                                        transition:'all .2s',
                                    }}
                                        onMouseEnter={e=>e.currentTarget.style.transform='translateY(-1px)'}
                                        onMouseLeave={e=>e.currentTarget.style.transform='translateY(0)'}
                                    >
                                        {plan.unit ? 'Solicitar este plan →' : 'Contactar ventas →'}
                                    </a>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ══ CÓMO FUNCIONA ══ */}
            <section className="ee-section" style={{ background:'#fff', padding:'80px clamp(20px,4vw,40px)' }}>
                <div style={{ maxWidth:'1180px', margin:'0 auto' }}>
                    <div style={{ textAlign:'center', marginBottom:'52px' }}>
                        <div style={{ display:'inline-flex', alignItems:'center', gap:'10px', marginBottom:'14px' }}>
                            <div style={{ height:'2px', width:'36px', background: colors.secondary, borderRadius:'2px' }}/>
                            <span style={{ fontSize:'.75rem', fontWeight:'700', letterSpacing:'.14em', textTransform:'uppercase', color: colors.secondary }}>
                                Proceso
                            </span>
                            <div style={{ height:'2px', width:'36px', background: colors.secondary, borderRadius:'2px' }}/>
                        </div>
                        <h2 style={{ fontSize:'clamp(1.8rem,3vw,2.6rem)', fontWeight:'900', color:'#0f172a', letterSpacing:'-.02em' }}>
                            Empezás a trabajar en{' '}
                            <span style={{ color: colors.secondary }}>4 pasos simples</span>
                        </h2>
                    </div>

                    <div className="ee-steps-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'20px' }}>
                        {STEPS.map((s, i) => (
                            <div key={s.n} className="ee-timeline-item" style={{ position:'relative' }}>
                                {/* Connector */}
                                {i < STEPS.length - 1 && (
                                    <div style={{ position:'absolute', top:'22px', left:'calc(50% + 22px)', right:'-50%', height:'2px', background:`${s.color}30`, zIndex:0 }}/>
                                )}
                                <div style={{ position:'relative', zIndex:1 }}>
                                    <div className="ee-timeline-dot" style={{
                                        width:'44px', height:'44px', borderRadius:'50%',
                                        background:`linear-gradient(135deg, ${s.color}, ${s.color}cc)`,
                                        display:'flex', alignItems:'center', justifyContent:'center',
                                        color:'#fff', fontWeight:'900', fontSize:'.85rem',
                                        margin:'0 auto 16px', boxShadow:`0 4px 14px ${s.color}30`,
                                    }}>{s.n}</div>
                                    <div style={{ background:'#fff', borderRadius:'16px', padding:'20px', boxShadow:'0 2px 12px rgba(0,0,0,.04)', border:'1.5px solid #f1f5f9', borderTop:`3px solid ${s.color}` }}>
                                        <h3 style={{ fontSize:'.9rem', fontWeight:'800', color:'#0f172a', marginBottom:'8px' }}>{s.title}</h3>
                                        <p style={{ fontSize:'.78rem', color:'#64748b', lineHeight:1.6 }}>{s.desc}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ══ CLIENTES ══ */}
            <section className="ee-section" style={{ background:'#fff', padding:'72px clamp(20px,4vw,40px)' }}>
                <div style={{ maxWidth:'1180px', margin:'0 auto' }}>
                    <p style={{ textAlign:'center', fontSize:'.78rem', fontWeight:'700', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.12em', marginBottom:'28px' }}>
                        Empresas que ya trabajan con nosotros
                    </p>
                    <div style={{ display:'flex', justifyContent:'center', flexWrap:'wrap', gap:'16px' }}>
                        {CLIENTS.map(c => (
                            <div key={c.name} style={{
                                background:'#f8fafc', borderRadius:'14px', padding:'14px 20px',
                                display:'flex', alignItems:'center', gap:'10px',
                                border:'1.5px solid #f1f5f9',
                                transition:'all .2s',
                            }}
                                onMouseEnter={e=>{ e.currentTarget.style.borderColor=`${c.color}30`; e.currentTarget.style.boxShadow=`0 4px 16px ${c.color}10`; }}
                                onMouseLeave={e=>{ e.currentTarget.style.borderColor='#f1f5f9'; e.currentTarget.style.boxShadow='none'; }}
                            >
                                <div style={{ width:'36px', height:'36px', borderRadius:'10px', background:`linear-gradient(135deg, ${c.color}22, ${c.color}10)`, border:`1.5px solid ${c.color}20`, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:'800', fontSize:'.8rem', color: c.color }}>
                                    {c.initial}
                                </div>
                                <span style={{ fontSize:'.85rem', fontWeight:'700', color:'#0f172a' }}>{c.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ══ CTA FINAL ══ */}
            <section className="ee-section" style={{ background:`linear-gradient(135deg, #7a4800, ${colors.accentDark} 55%, ${colors.accent})`, padding:'72px clamp(20px,4vw,40px)' }}>
                <div style={{ maxWidth:'800px', margin:'0 auto', textAlign:'center' }}>
                    <div style={{ fontSize:'2.8rem', marginBottom:'16px' }}>🏢</div>
                    <h2 style={{ fontSize:'clamp(1.8rem,3vw,2.6rem)', fontWeight:'900', color:'#fff', letterSpacing:'-.025em', marginBottom:'14px' }}>
                        Hablemos de tu empresa
                    </h2>
                    <p style={{ fontSize:'1rem', color:'rgba(255,255,255,.85)', lineHeight:1.75, marginBottom:'32px', maxWidth:'480px', margin:'0 auto 32px' }}>
                        Nuestro equipo de ventas corporativas te contacta en menos de 2 horas. Diagnóstico gratuito, sin compromiso.
                    </p>
                    <div className="ee-cta-row" style={{ display:'flex', gap:'12px', justifyContent:'center', flexWrap:'wrap' }}>
                        <a href="/contacto" style={{
                            display:'inline-flex', alignItems:'center', gap:'8px',
                            background:'#fff', color: colors.accentDark,
                            borderRadius:'13px', padding:'14px 28px',
                            fontWeight:'900', fontSize:'1rem', textDecoration:'none',
                            boxShadow:'0 6px 20px rgba(0,0,0,.15)', transition:'all .22s',
                        }}
                            onMouseEnter={e=>e.currentTarget.style.transform='translateY(-2px)'}
                            onMouseLeave={e=>e.currentTarget.style.transform='translateY(0)'}
                        >
                            <Mail size={16}/> Solicitar diagnóstico →
                        </a>
                        <a href="tel:+59176204601" style={{
                            display:'inline-flex', alignItems:'center', gap:'8px',
                            background:'rgba(255,255,255,.15)', backdropFilter:'blur(8px)',
                            border:'1.5px solid rgba(255,255,255,.35)', color:'#fff',
                            borderRadius:'13px', padding:'14px 24px',
                            fontWeight:'700', fontSize:'.95rem', textDecoration:'none', transition:'all .22s',
                        }}
                            onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,.25)'}
                            onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,.15)'}
                        >
                            <Phone size={15}/> +591 762 04601
                        </a>
                    </div>

                    {/* Barra colores */}
                    <div style={{ display:'flex', gap:'5px', justifyContent:'center', marginTop:'40px' }}>
                        {[colors.accentDark, colors.primary, colors.secondary, colors.secondaryLight].map((_,i) => (
                            <div key={i} style={{ height:'3px', width:i===0?'28px':'10px', borderRadius:'3px', background:'rgba(255,255,255,.5)' }}/>
                        ))}
                    </div>
                </div>
            </section>

        </div>
    );
};

export default EspaciosEmpresarial;